import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";

/**
 * One place for a tool's "About" text. Each tool file still declares its
 * own copy through ToolInfoSection / ToolWithSidebar (so the text stays in
 * that tool's lazy chunk), but instead of rendering it wherever the tool
 * file happens to put it, those components register it here and ToolPage
 * renders it once, in the same place, for every tool.
 */
export interface ToolInfo {
  whatIsIt: string;
  howToUse?: string;
  tip?: string;
  disclaimer?: string;
}

type Register = (info: ToolInfo | null) => void;
const RegisterContext = createContext<Register | null>(null);

/**
 * `toolId` ties the registered text to the tool that registered it: when the person moves to another tool
 * ("Continue with ..."), the previous tool's text is not shown while the next tool's code is still loading.
 */
export function useToolInfoState(toolId = "") {
  const [state, setState] = useState<{ toolId: string; info: ToolInfo | null }>({ toolId, info: null });
  const register = useCallback<Register>((next) => setState({ toolId, info: next }), [toolId]);
  const info = state.toolId === toolId ? state.info : null;
  const Provider = useMemo(
    () =>
      function ToolInfoProvider({ children }: { children: ReactNode }) {
        return <RegisterContext.Provider value={register}>{children}</RegisterContext.Provider>;
      },
    [register],
  );
  return { info, Provider };
}

/** Registers the info with the page. Returns true when a page is collecting it (so the caller renders nothing inline). */
export function useRegisterToolInfo(info: ToolInfo): boolean {
  const register = useContext(RegisterContext);
  const { whatIsIt, howToUse, tip, disclaimer } = info;
  useEffect(() => {
    if (!register) return;
    register({ whatIsIt, howToUse, tip, disclaimer });
    return () => register(null);
  }, [register, whatIsIt, howToUse, tip, disclaimer]);
  return register !== null;
}

/** Wrap a tool that is shown inside another page (a connected app step) so its About text is not collected or shown. */
export function SuppressToolInfo({ children }: { children: ReactNode }) {
  return <RegisterContext.Provider value={() => undefined}>{children}</RegisterContext.Provider>;
}
